
import Vue from "vue";
import store from "@/store";
import { UI } from "@/store/ui";
import { LOYALTY } from "@/store/loyalty";
import { createNamespacedHelpers } from "vuex";
import { ILoyaltyState, ISegment, OfferLevelType } from "@/store/loyalty/types";
import { required } from "vuelidate/lib/validators";
import { onlyDigits, multipleOfTen } from "@/validators";
import { LOYALTY_ROUTE_NAMES } from "@/loyalty-routes";
import isEqual from "lodash/isEqual";
import { standardOfferAccess } from "@/helpers/loyaltyEditAccess";

const { mapGetters, mapState } = createNamespacedHelpers(LOYALTY.NAME_SPACE);

export default Vue.extend({
  props: {
    segmentOfferId: {
      type: [Number, String]
    }
  },
  computed: {
    isEligible: {
      get() {
        return this.segmentOffer && this.segmentOffer.isEligible;
      },
      set(value: boolean) {
        this.$store.commit(
          LOYALTY.NS(LOYALTY.MUTATIONS.SET_SEGMENT_OFFER_IS_ELIGIBLE),
          value
        );
      }
    },
    isLoading(): boolean {
      const { ui, loyalty } = this.$store.state;

      return ui.isloading || loyalty.isLoading;
    },
    readonlyAccess() {
      return !standardOfferAccess();
    },
    isFormDisabled() {
      return !this.isEligible || this.isLoading || this.readonlyAccess;
    },
    isButtonDisabled() {
      return this.$v.$invalid || this.isLoading || this.readonlyAccess;
    },
    hasChanges(): boolean {
      const changes = this.segmentOffer;
      const original = this.segmentOfferOriginal;

      return !isEqual(original, changes);
    },
    offerLevelType: {
      get() {
        return this.segmentOffer && this.segmentOffer.level;
      },
      set(value: string) {
        this.$store.commit(
          LOYALTY.NS(LOYALTY.MUTATIONS.SET_SEGMENT_OFFER_LEVEL_TYPE),
          value
        );
      }
    },
    flyBuysEarnRateQuantity: {
      get() {
        return this.segmentOffer && this.segmentOffer.flyBuysEarnRateQuantity;
      },
      set(value: string) {
        this.$store.commit(
          LOYALTY.NS(
            LOYALTY.MUTATIONS.SET_SEGMENT_OFFER_FLYBUYS_EARN_RATE_QUANTITY
          ),
          value
        );
      }
    },
    airpointsEarnRateQuantity: {
      get() {
        return this.segmentOffer && this.segmentOffer.airpointsEarnRateQuantity;
      },
      set(value: string) {
        this.$store.commit(
          LOYALTY.NS(
            LOYALTY.MUTATIONS.SET_SEGMENT_OFFER_AIRPOINTS_EARN_RATE_QUANTITY
          ),
          value
        );
      }
    },
    channels: {
      get() {
        return this.segmentOffer && this.segmentOffer.channels;
      },
      set(value: any[]) {
        this.$store.commit(
          LOYALTY.NS(LOYALTY.MUTATIONS.SET_SEGMENT_OFFER_CHANNELS),
          value
        );
      }
    },
    offerLevelTypeList: () => OfferLevelType,
    ...mapState({
      channelList: (state: ILoyaltyState) => state.channelList,
      segmentList: (state: ILoyaltyState) => state.segmentList,
      segmentOffer: (state: ILoyaltyState) => state.segmentOffer,
      segmentOfferOriginal: (state: ILoyaltyState) => state.segmentOfferOriginal
    })
  },
  methods: {
    onCancelChanges() {
      const originalData = this.segmentOfferOriginal;

      this.$store.commit(
        LOYALTY.NS(LOYALTY.MUTATIONS.SET_SEGMENT_OFFER),
        originalData
      );
    },
    async onSaveChanges() {
      this.$store.dispatch(LOYALTY.NS(LOYALTY.ACTIONS.TOGGLE_IS_LOADING), true);

      const success = await this.$store.dispatch(
        LOYALTY.NS(LOYALTY.ACTIONS.UPDATE_SEGMENT_OFFER),
        this.segmentOfferId
      );

      this.$store.dispatch(
        LOYALTY.NS(LOYALTY.ACTIONS.TOGGLE_IS_LOADING),
        false
      );

      if (success) {
        this.$router.push({
          name: LOYALTY_ROUTE_NAMES.SEGMENT_OFFERS
        });
      }
    }
  },
  validations() {
    // only do validation when isEligible
    if (this.isEligible) {
      return {
        offerLevelType: {
          required
        },
        channels: {
          required
        },
        flyBuysEarnRateQuantity: {
          required,
          onlyDigits,
          multipleOfTen
        },
        airpointsEarnRateQuantity: {
          required,
          onlyDigits,
          multipleOfTen
        }
      };
    } else {
      return {};
    }
  }
});
